/**
 * Route configuration for the web app.
 *
 * Exported as a plain `RouteObject[]` rather than a fully built `Router`
 * instance. The router is created inside `main.tsx` so its (transitive,
 * non-portable) `@remix-run/router` type stays local to that file.
 *
 * Phase 1 ships the public Landing + Login + NotFound, plus role-protected
 * placeholders for /order, /kitchen, and /admin. The placeholders enforce
 * RBAC today; their full UIs land in later phases.
 */
import type { RouteObject } from 'react-router-dom';
import { AdminLayout } from './components/admin/AdminLayout';
import { Layout } from './components/Layout';
import { RoleGuard } from './components/RoleGuard';
import { AdminDashboard } from './pages/admin/Dashboard';
import { AdminMenuManager } from './pages/admin/MenuManager';
import { AdminOrderLog } from './pages/admin/OrderLog';
import { AdminSettings } from './pages/admin/Settings';
import { AdminUsers } from './pages/admin/Users';
import { Checkout } from './pages/Checkout';
import { Kitchen } from './pages/Kitchen';
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { Menu } from './pages/Menu';
import { NotFound } from './pages/NotFound';
import { OrderHistory } from './pages/OrderHistory';
import { OrderTracking } from './pages/OrderTracking';
import { Privacy } from './pages/Privacy';
import { Profile } from './pages/Profile';
import { ProfileSetup } from './pages/ProfileSetup';
import { PublicMenu } from './pages/PublicMenu';

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Layout />,
    errorElement: <NotFound />,
    children: [
      { index: true, element: <Landing /> },
      { path: 'login', element: <Login /> },
      { path: 'privacy', element: <Privacy /> },
      { path: 'menu', element: <PublicMenu /> },
      {
        path: 'profile/setup',
        element: (
          <RoleGuard allow={['teacher', 'admin']}>
            <ProfileSetup />
          </RoleGuard>
        ),
      },
      {
        path: 'profile',
        element: (
          <RoleGuard allow={['teacher', 'admin']}>
            <Profile />
          </RoleGuard>
        ),
      },
      {
        path: 'order',
        element: (
          <RoleGuard allow={['teacher', 'admin']}>
            <Menu />
          </RoleGuard>
        ),
      },
      {
        path: 'checkout',
        element: (
          <RoleGuard allow={['teacher', 'admin']}>
            <Checkout />
          </RoleGuard>
        ),
      },
      {
        path: 'orders',
        element: (
          <RoleGuard allow={['teacher', 'admin']}>
            <OrderHistory />
          </RoleGuard>
        ),
      },
      {
        path: 'orders/:orderId',
        element: (
          <RoleGuard allow={['teacher', 'admin']}>
            <OrderTracking />
          </RoleGuard>
        ),
      },
      {
        path: 'kitchen',
        element: (
          <RoleGuard allow={['barista', 'admin']}>
            <Kitchen />
          </RoleGuard>
        ),
      },
      {
        path: 'admin',
        element: (
          <RoleGuard allow={['admin']}>
            <AdminLayout />
          </RoleGuard>
        ),
        children: [
          { index: true, element: <AdminDashboard /> },
          { path: 'menu', element: <AdminMenuManager /> },
          { path: 'orders', element: <AdminOrderLog /> },
          { path: 'users', element: <AdminUsers /> },
          { path: 'settings', element: <AdminSettings /> },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
];
